import React, { useState, useEffect, useRef } from "react";
import { Modal, Alert } from 'antd';
import "./modal.css"
import 'react-quill/dist/quill.snow.css';
import Cookies from "universal-cookie";
import { sendEmails } from "../helper/emailHelper.js";
import { convertToLocalDate, getCurrentDate, optionalValidation, getFirstLetterUppercase } from "../helper/object-validation-helper.js";
import Loader from "../components/Loader.js";
import { logo } from "../assets/svg/svg.js";
import { transactionAction } from "../helper/chain-helper.js";
import { getHostContract, getHostHelperContract, getSuggestion } from "../helper/contract-helper.js";
import { folders } from "../assets/svg/folder-svg.js";
import { getFolderIconsByColor } from "../assets/svg/follder-svg-helper.js";
import { encryptFilesWithDek, getFile } from '../helper/attachmentHelper.js';
import { isDekWriteEnabled } from '../helper/dekManager.js';
import { sendAttachment } from '../service/api-actions.js';
import { saveSenderEncryptedEmail } from "../helper/emailHelper.js";
import { useDispatch, useSelector } from 'react-redux';
import FileViewer from "react-file-viewer";
import Editor from './Editor';
import { toast, Bounce } from 'react-toastify';
import { getGeneralSettings, getSendAsEmails } from '../helper/settingsHelper.js';
import { Calendar as CalendarIcon } from "lucide-react";
import { Tooltip } from "antd";
import "../../src/assets/css/calendar.css"
import { reportError, toErrorString } from "../utils/errorLogger.js";
import { notifyChainFailure } from "../utils/txFeedback.js";
import { clearReplyBuffer } from '../helper/composeBuffer.js';
const cookies = new Cookies();

// Reply-all recipient computation. Pure, and outside the component, so the initial pre-fill and the late
// "the send-as alias list finally arrived" pass both run the SAME algorithm over the SAME immutable source
// (the stored header). Recomputing from the header is what keeps this safe: a pass that instead patched up
// the values already sitting in the Recipient/CC boxes is how a real participant gets dropped.
// `selfAddresses` is every lower-cased address that belongs to the signed-in user. It is allowed to be
// incomplete (or empty) while the alias list is still loading — an incomplete set can only leave an extra
// copy of the user's own address in To/CC, it can never remove somebody else.
const computeReplyAllRecipients = (header, selfAddresses = []) => {
  const isSelf = (address) => selfAddresses.includes(String(address ?? '').trim().toLowerCase());
  // header.to / header.cc are arrays (helper/emailHelper.js), but an unused field arrives as [""] because
  // compose splits an empty string — strip blanks before anything is counted or sent.
  const cleanList = (list) => (Array.isArray(list) ? list : String(list ?? '').split(','))
    .map((address) => String(address ?? '').trim())
    .filter((address) => address !== '');
  const dedupe = (list) => list.filter((address, index) =>
    list.findIndex((other) => other.toLowerCase() === address.toLowerCase()) === index);
  const originalSender = String(header?.sender ?? '').trim();
  // Sender leads, then the rest of To. De-duplicated because the sender can also appear in To.
  const toRecipients = dedupe(
    [originalSender, ...cleanList(header?.to)].filter((address) => address !== '' && !isSelf(address))
  );
  // Reply-all on a note that only reached the user's own addresses would otherwise leave To empty and the
  // send would be refused with "Please specify at least one recipient".
  const finalTo = toRecipients.length ? toRecipients : (originalSender ? [originalSender] : []);
  const finalCC = dedupe(cleanList(header?.cc).filter((address) => !isSelf(address)))
    .filter((address) => !finalTo.some((existing) => existing.toLowerCase() === address.toLowerCase()));
  return { to: finalTo, cc: finalCC };
};

const ReplyModal = (params) => {
  const [sender, setSender] = useState('');
  const [receiver, setReceiver] = useState('');
  const [initialReceiver, setInitialReceiver] = useState('');
  const [subject, setSubject] = useState('');
  const [senderLoader, setSenderLoader] = useState('');
  const [check, setCheck] = useState(true);
  const [onclear, setonclear] = useState(true);
  const [user] = useState(cookies.get("userObject"));
  const isReply = params.messageType === "Reply" || "Forward";
  const [loader, setLoader] = useState(false);
  const headerJson = JSON.parse(optionalValidation(params, "msg.header"));
  const created_at = optionalValidation(params, "msg.created_at");
  const decryptedMessage = optionalValidation(params, "decryptedMessage");
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [initialFile, setInitialFile] = useState([]);
  const [accountSettings, setAccountSettings] = useState([]);
  const [CC, setCC] = useState('');
  const [BCC, setBCC] = useState('');
  const [isCC, setIsCC] = useState(false);
  const [isBCC, setIsBCC] = useState(false);
  const [focused, setFocused] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [inputType, setInputType] = useState('');
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState({
    receiver: false,
    cc: false,
    bcc: false
  });
  const [sendAsEmail, setSendAsEmail] = useState([]);
  const [fromAddress, setFromAddress] = useState(user?.name);
  // Every address that is "me" for reply-all: the signed-in address plus every VERIFIED send-as alias.
  // Held in a ref rather than read out of the `sendAsEmail` state because the pre-fill effect below also
  // re-downloads attachments — adding a state dependency to it would re-run getfile() and duplicate them.
  // `null` means the alias list has not come back from chain yet, which is a different thing from "no aliases".
  const selfAddressesRef = useRef(null);
  // Bumped once the alias list lands, so the pre-fill can be redone with the real answer instead of
  // silently depending on whether the chain call happened to beat the user's Reply-all click.
  const [selfAddressesVersion, setSelfAddressesVersion] = useState(0);
  // Set as soon as the user edits Recipient/CC, so a late alias refinement can never overwrite their edit.
  const recipientsEditedRef = useRef(false);

  const formattedDate = convertToLocalDate(created_at);
  const isMessageInclude = useSelector((state) => state.isMessageInclude);
  useEffect(() => {
    setSettingsJson();
  }, []);

  // "Me" is the userObject cookie's name — the same value this component already treats as the signed-in
  // address for the Sender field and the send-as default; UserName in localStorage is written from the same
  // object at sign-in (routes/SignIn.js, components/Profile.js) and covers a cleared cookie.
  const getPrimaryAddress = () => (user?.name || localStorage.getItem('UserName') || '').trim().toLowerCase();

  // ONLY verified aliases count as the user's own address. An unverified entry is an unproven claim on a
  // mailbox that belongs to somebody else — AddEmailModal writes verify:"false" and mails a confirmation
  // link to that address, and only its owner clicking through flips it to "true" (routes/ConfirmSendAs.js).
  // Treating an unverified entry as "me" would silently delete a real thread participant from the reply.
  const resolveSelfAddresses = (aliasList) => {
    const primary = getPrimaryAddress();
    const addresses = primary ? [primary] : [];
    (Array.isArray(aliasList) ? aliasList : []).forEach((entry) => {
      const isVerified = entry?.verify === true || String(entry?.verify ?? '').toLowerCase() === 'true';
      const address = String(entry?.email ?? '').trim().toLowerCase();
      if (isVerified && address && !addresses.includes(address)) addresses.push(address);
    });
    return addresses;
  };

  // Whatever is known right now. Before the alias list resolves this is the primary address alone, which is
  // the safe direction to fail: the reply may still carry one of the user's own aliases (a nuisance they can
  // see and delete), but it can never be missing a real participant.
  const getSelfAddresses = () => {
    if (selfAddressesRef.current) return selfAddressesRef.current;
    const primary = getPrimaryAddress();
    return primary ? [primary] : [];
  };

  // Reply-all must drop ME and keep EVERYONE else. The original code was `senderRes.to.slice(1)` — an
  // unconditional "drop whoever happens to be first in To" — which silently deleted a real thread
  // participant whenever the signed-in user was not first, and still addressed the reply back to the user.
  const applyReplyAllRecipients = (selfAddresses) => {
    const header = JSON.parse(params.msg.header);
    const { to, cc } = computeReplyAllRecipients(header, selfAddresses);
    const toValue = to.join(',');
    const ccValue = cc.join(',');
    setInitialReceiver(toValue);
    setReceiver(toValue);
    setCC(ccValue);
    // The CC box is hidden by default and the Send handler reads `isCC ? CC : ""`, so a pre-filled CC that
    // stays hidden is never actually sent — reply-all was dropping every CC recipient outright. Reveal it
    // when there is something to show, so the user can review and edit the list before sending.
    setIsCC(ccValue.length > 0);
    // Never propagate BCC. Copies delivered to recipients carry bcc: [] (helper/emailHelper.js), but the
    // sender's own Sent copy carries the real list — so this is pinned empty rather than left to the data:
    // a BCC'd recipient hitting reply-all must never reveal the other BCC'd recipients.
    setBCC('');
    // These values are ours, not the user's, so a later refinement is still allowed to replace them.
    recipientsEditedRef.current = false;
  };

  async function setSettingsJson() {

    try {
      const generalSettings = await getGeneralSettings();
      setAccountSettings(generalSettings);
      const sendAsEmail = await getSendAsEmails();
      // Resolve the identity set from the raw list BEFORE it is handed to state, because the effect that
      // watches `sendAsEmail` mutates the array in place (unshift of the primary address).
      if (Array.isArray(sendAsEmail)) {
        selfAddressesRef.current = resolveSelfAddresses(sendAsEmail);
        setSelfAddressesVersion((version) => version + 1);
      }
      setSendAsEmail(sendAsEmail);
    } catch (error) {
      const errorStr = toErrorString(error || error.message);
      const userName = localStorage.getItem('UserName');
      reportError({
        endpoint: '-',
        message: `FileName: common-alert.js, Fun: setSettingsJson, ${String(error.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
    }
  }

  useEffect(() => {
    if (sendAsEmail?.length) {
      sendAsEmail.unshift({
        email: user.name,
        isDefault: true
      });
      const displayEmail = sendAsEmail?.find(email => email.isDefault === 'true')?.email;
      sendAsEmail?.forEach(email => {
        email.isDefault = email.email === displayEmail ? 'true' : 'false';
      })
      if (!displayEmail) {
        sendAsEmail.forEach(email => {
          email.isDefault = email.email === user.name ? 'true' : 'false';
        })
        setFromAddress(user.name);
      } else {
        setFromAddress(displayEmail)
      }
    }
  }, [sendAsEmail])



  const handleClickOutsideSidebar = (e) => {
    if (!e.target.classList.contains("suggestion-list-email") && !e.target.classList.contains("suggestion-input")) {
      setIsFocused({
        receiver: false,
        cc: false,
        bcc: false
      });
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutsideSidebar);

    return () => {
      document.removeEventListener("click", handleClickOutsideSidebar);
    };
  }, []);
  useEffect(() => {
    setSender(user && user.name);
    if (params.pageType === 'Sent') {
      const senderNameValue = params.msg?.receiver ? JSON.parse(params.msg.receiver).to.concat(JSON.parse(params.msg.receiver).cc, JSON.parse(params.msg.receiver).bcc).filter(value => value)[0] : '';
      setInitialReceiver(senderNameValue);
      setReceiver(senderNameValue);
    } else {
      if (params.messageType === "ReplyAll") {
        applyReplyAllRecipients(getSelfAddresses());
      } else if (params.messageType === "Reply") {
        const senderRes = JSON.parse(params.msg.header)
        const result = [senderRes.sender]
        setInitialReceiver(`${result}`);
        setReceiver(`${result}`);
      }
      else {
        const senderNameValue = isReply ? optionalValidation(params, "msg.sender") : "";
        setInitialReceiver(senderNameValue);
        setReceiver(senderNameValue);
      }

    }
    if (headerJson && headerJson.subject.startsWith("Re: ") || headerJson && headerJson.subject.startsWith("FW: ")) {
      headerJson.subject = headerJson.subject.substring(4);
    }
    if (headerJson && headerJson.subject) {

      if (params.messageType === "Reply") {
        setSubject(`Re: ${headerJson.subject}`);
      }
      else if (params.messageType === "ReplyAll") {
        setSubject(`Re: ${headerJson.subject}`);
      }
      else {
        setReceiver('');
        setSubject(`FW: ${headerJson.subject}`);
      }
    }
    if (params.attachmentValue && params.isModalOpen && isMessageInclude) {
      getfile();
    } else if (params.attachmentValue && params.isModalOpen && !isMessageInclude) {
      setInitialFiles(initialFile);
    }
    setCheck(isMessageInclude);
    setonclear(params.isModalOpen);
  }, [params.isModalOpen, isMessageInclude]);

  // The send-as alias list is fetched from chain on mount, but the pre-fill above runs when the user clicks
  // Reply-all. Nothing orders those two, so with a slow chain the pre-fill can run knowing only the primary
  // address and leave one of the user's own verified aliases in To — the reply goes back to the user. This
  // redoes the pre-fill once, at the moment the alias list actually lands, so the outcome stops depending on
  // that race. Deliberately NOT folded into the effect above: that one also calls getfile(), and re-running
  // it would append the attachments a second time.
  useEffect(() => {
    // Still unresolved (or the chain call failed) — leave the pre-fill alone; keeping the user's own alias in
    // To is the safe failure, dropping a real participant is not.
    if (!selfAddressesRef.current) return;
    if (!params.isModalOpen || params.pageType === 'Sent') return;
    if (params.messageType !== "ReplyAll" || !params.msg?.header) return;
    // The user has already started editing the recipients; their list wins over ours.
    if (recipientsEditedRef.current) return;
    try {
      applyReplyAllRecipients(selfAddressesRef.current);
    } catch (error) {
      const errorStr = toErrorString(error || error.message);
      const userName = localStorage.getItem('UserName');
      reportError({
        endpoint: '-',
        message: `FileName: common-alert.js, Fun: refineReplyAllRecipients, ${String(error.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
        level: 'warn',
      });
    }
    // Dependency list is deliberately just the version counter, despite the exhaustive-deps warning: this
    // must fire once, when the answer arrives. Widening it to `params`/`applyReplyAllRecipients` makes it run
    // on ordinary re-renders, and it would then slam the CC box shut again every time the user clicks CC open.
  }, [selfAddressesVersion]);

  useEffect(() => {
    if (!params.isModalOpen) {
      setAttachmentFiles([]);
      setInitialFile([]);
      setFocused(true);
      setIsCC(false);
      setIsBCC(false);
      setBCC('');
      setCC('');
    }
    if (params.isModalOpen) {
      getSuggestionList();
    }

  }, [params.isModalOpen]);

  const getSuggestionList = async () => {
    try {
      const suggestionList = await getSuggestion();
      setEmailSuggestions(suggestionList);
    } catch (e) {
      const userName = localStorage.getItem('UserName');
      const errorStr = toErrorString(e || e.message);
      reportError({
        endpoint: '-',
        message: String(e.message || 'Unhandled error'),
        error: errorStr,
        userName: userName || '',
        level: 'warn',
      });
      console.error('error fetch suggestion', e);
    }
  }

  const setInitialFiles = async () => {
    if (params.attachmentValue && params.attachmentValue.length) {
      setLoader(true);
      const filesPromises = params.attachmentValue.map(async (file) => {
        const key = Object.keys(file)[0];
        const value = await getFile(key, file[key]);
        return value;
      });
      const files = await Promise.all(filesPromises);
      setInitialFile(files)
      setLoader(false);
    }
  }

  const getfile = async () => {
    try {
      if (params.attachmentValue && params.attachmentValue.length) {
        setLoader(true);
        const filesPromises = params.attachmentValue.map(async (file) => {
          const key = Object.keys(file)[0];
          const value = await getFile(key, file[key]);
          return value;
        });
        const files = await Promise.all(filesPromises);
        const combinedFiles = [...attachmentFiles, ...files];
        setInitialFile(files)
        setAttachmentFiles(combinedFiles);
        setLoader(false);
      } else if (initialFile.length) {
        setAttachmentFiles(initialFile);
        setLoader(false);
      }
    } catch (e) {
      const errorStr = toErrorString(e || e.message);
      const userName = localStorage.getItem('UserName');
      reportError({
        endpoint: '-',
        message: `FileName: common-alert.js, Fun: getfile, ${String(e.message || 'Unhandled error')}`,
        error: errorStr,
        userName: userName || '',
      });
      console.error('error fetch file', e);
      // params.close();
      setLoader(false);
    }
  };
  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    const combinedFiles = [...attachmentFiles, ...newFiles];
    const maxSize = 25 * 1024 * 1024; // 25 MB in bytes
    const totalSize = combinedFiles.reduce((acc, file) => acc + file.size, 0);
    if (totalSize > maxSize) {
      toast.error("Total file size exceeds the limit of 25 MB.", {
        position: "top-center",
        transition: Bounce,
      });
      return;
    }
    setAttachmentFiles(combinedFiles);
  };
  const handleRemoveFile = (indexToRemove) => {
    setAttachmentFiles(prevFiles => prevFiles.filter((file, index) => index !== indexToRemove));
  }

  const setAttachment = async (value) => {
    setCheck(value);
    if (value === true) {
      await getfile();
    } else {
      setAttachmentFiles([]);
    }
  }

  const handleInputChange = (value, setValue, fieldType) => {
    // Once the user has touched the address fields, the late send-as-alias refinement must not replace what
    // they typed — a half-typed address being wiped mid-keystroke is worse than an alias left in the list.
    if (fieldType === 'receiver' || fieldType === 'cc') recipientsEditedRef.current = true;
    setValue(value);
    const lastEmailPart = value.split(',').pop().trim();
    if (lastEmailPart) {
      setSuggestions(emailSuggestions.filter((email) =>
        email.toLowerCase().includes(lastEmailPart.toLowerCase()) &&
        email.toLowerCase() !== lastEmailPart.toLowerCase()
      ));
      setInputType(fieldType);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (email) => {
    if (inputType === 'receiver') {
      const updatedReceivers = receiver.split(',');
      updatedReceivers.pop();
      updatedReceivers.push(email);
      setReceiver(updatedReceivers.join(',') + ',');
      localStorage.setItem("recipient", updatedReceivers.join(',') + ',');
    } else if (inputType === 'cc') {
      const updatedCC = CC.split(',');
      updatedCC.pop();
      updatedCC.push(email);
      setCC(updatedCC.join(',') + ',');
      localStorage.setItem("cc", updatedCC.join(',') + ',');
    } else if (inputType === 'bcc') {
      const updatedBCC = BCC.split(',');
      updatedBCC.pop();
      updatedBCC.push(email);
      setBCC(updatedBCC.join(',') + ',');
      localStorage.setItem("bcc", updatedBCC.join(',') + ',');
    }
    setSuggestions([]);

  };

  const handleFocus = (fieldType) => {
    setSuggestions([]);
    setIsFocused(prevState => ({
      receiver: false,
      cc: false,
      bcc: false,
    }));
    setIsFocused(prevState => ({ ...prevState, [fieldType]: true }));
  };


  return (
    <div>
      <Modal className="modal-send-email-header parent-div-content-reply-mail" open={params.isModalOpen} onOk={params.close} onCancel={params.close} footer={null}>
        <div className="parent-div-content-reply-mail-child" id='reply-modal'>
          {isReply &&
            <div className='label-title-check-box'> Include Previous Messages
              <input
                type="checkbox"
                id="check-box-reply-email"
                checked={check}
                onChange={() => {
                  setAttachment(!check);
                }}
              />
            </div>
          }
          <div className='send-alert-body-content connect-wallet-account-div reply-email-user reply-email'>
            <div className="email-username reply-user">
              <span>Sender:</span>
              {sendAsEmail?.length ?
                <select className="from-user-select compose" id={"sender"} value={fromAddress} onChange={(e) => { setFromAddress(e?.target?.value) }} >
                  {sendAsEmail?.map((data, index) => {
                    return (
                      <option key={index} defaultValue={() => {
                        if (data.isDefault === 'true' || data.isDefault === true) {
                          return data.email
                        }
                      }} value={data.email}>{data.email}</option>
                    )
                  })}
                </select> :
                <input className="reply-user-input reply" value={sender} readOnly />}
            </div>
            <div className="email-username reply-user">
              <span >Recipient:</span>
              <input className="reply-user-input suggestion-input reply" onChange={(e) => {
                handleInputChange(e.target.value, setReceiver, 'receiver')
              }} value={receiver} onFocus={() => handleFocus('receiver')} />
              {!accountSettings.isSecureSent && (
                <>
                  <span className="compose-cc-bcc" onClick={() => setIsCC(!isCC)}> CC </span>
                  <span className="compose-cc-bcc" onClick={() => setIsBCC(!isBCC)} > BCC </span>
                </>
              )}
              {isFocused.receiver && suggestions.length > 0 && (
                <div className="suggestion-list">
                  <div className="suggestion-body">
                    {suggestions.map((email, index) => (
                      <div className="suggestion-list-email" key={index} onClick={() => handleSuggestionClick(email)}>
                        <div className="user-profile-pic">{email.charAt(0).toUpperCase()}</div> <div>{email}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {isCC &&
              <div className="email-username reply-user">
                <span>CC:</span>
                <input className="reply-user-input suggestion-input reply" value={CC} onChange={(e) => {
                  setFocused(false);
                  handleInputChange(e.target.value, setCC, 'cc')
                }} onFocus={() => handleFocus('cc')} />
                {isFocused.cc && suggestions.length > 0 && (
                  <div className="suggestion-list">
                    <div className="suggestion-body">
                      {suggestions.map((email, index) => (
                        <div className="suggestion-list-email" key={index} onClick={() => handleSuggestionClick(email)}>
                          <div className="user-profile-pic">{email.charAt(0).toUpperCase()}</div> <div>{email}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            }

            {isBCC &&
              <div className="email-username reply-user">
                <span>BCC:</span>
                <input className="reply-user-input suggestion-input reply" value={BCC} onChange={(e) => {
                  setFocused(false);
                  handleInputChange(e.target.value, setBCC, 'bcc')
                }} onFocus={() => handleFocus('bcc')} />
                {isFocused.bcc && suggestions.length > 0 && (
                  <div className="suggestion-list">
                    <div className="suggestion-body">
                      {suggestions.map((email, index) => (
                        <div className="suggestion-list-email" key={index} onClick={() => handleSuggestionClick(email)}>
                          <div className="user-profile-pic">{email.charAt(0).toUpperCase()}</div> <div>{email}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            }
            <div className="email-username reply-user">
              <span>Topic:</span>
              <input className="reply-user-input reply" value={subject} onChange={(e) => {
                setSubject(e.target.value)
              }} />
            </div>
          </div>
          <Editor
            placeholder="Write something..."
            check={check}
            initialMessage={localStorage.getItem("replyEmail")}
            handleRemoveFile={handleRemoveFile}
            formattedDate={formattedDate}
            initialReceiver={initialReceiver || ''}
            decryptedMessage={decryptedMessage}
            onclear={onclear}
          />
          {attachmentFiles.length > 0 && (
            <div className="replay-attachment-file-rendering-method">
              <div className="replay-attachment-section-element">
                {attachmentFiles.map((file, index) => (
                  <a key={index}>{file.name}<span className="delete-icon cursor-pointer" onClick={() => handleRemoveFile(index)}> &#10006;</span></a>
                ))}
              </div>
            </div>
          )}
          <div className={`replay-send-section ${attachmentFiles.length ? 'has-attachments' : ''}`}>
            <Tooltip title="Create event">
              <div className="btn-event cursor-pointer  " onClick={() => {
                window.dispatchEvent(new Event("NEW_EVENT"));
              }}>
                <CalendarIcon />
              </div>
            </Tooltip>

            <div className="add-attachment-functionality">
              <div className="btn-attachment cursor-pointer" onClick={() => document.getElementById('fileInput').click()}>
                {logo.attachment_icon} <span className="add-file-component"> add file </span>
              </div>
              <input
                type="file"
                id="fileInput"
                multiple
                className="d-none"
                onChange={handleFileChange}
              />
            </div>
            <div className="send-reply-email" onClick={async () => {
              const isSavedOn = accountSettings.isSaveSent;
              setLoader(true);
              setonclear(false);
              const recipient = receiver;
              const cc = isCC ? CC : "";
              const bcc = isBCC ? BCC : "";
              let defaultEncryptedMessage = "MSG";
              if (isSavedOn) {
                const emailObject = { recipient: recipient, subject: subject, message: localStorage.getItem("replyEmail") };
                if (attachmentFiles && attachmentFiles.length) {
                  // The sender's OWN saved copy of the attachments — encrypted to OUR key (user.publicKey
                  // is the signed-in user's), stored for us, read back only by us. The RECIPIENTS' copies
                  // are a separate upload done by sendEmails() below, against each recipient's own public
                  // key, and must stay asymmetric.
                  //
                  // The branch lives HERE at the call site, mirroring emailHelper.js: sendAttachment /
                  // encryptFiles / encryptText are SHARED with the recipient path and are not touched, so
                  // nothing recipient-bound can reach encryptFilesWithDek by any argument.
                  //
                  // Without this the cutover was half-wired: saveSenderEncryptedEmail() below already goes
                  // v2/DEK-sealed when the flag flips, so this copy's BODY was sealed while its ATTACHMENT
                  // was still v1 asymmetric.
                  let attachmentResult;
                  try {
                    if (isDekWriteEnabled()) {
                      attachmentResult = await encryptFilesWithDek(attachmentFiles);
                    } else {
                      attachmentResult = await sendAttachment(attachmentFiles, user.publicKey);
                    }
                  } catch (err) {
                    // These now THROW when an upload fails instead of quietly returning the survivors
                    // (attachmentHelper.js). Nothing has been sent yet, so abort the reply and tell the user
                    // which files failed — sending a reply that is silently missing attachments while
                    // showing "Message Sent" is exactly what this replaces. This handler has no outer
                    // try/catch, so without this the loader would also hang forever.
                    console.error(err);
                    setLoader(false);
                    const failed = err?.failedFiles?.length ? `: ${err.failedFiles.join(", ")}` : "";
                    toast.error(`Message not sent — these attachments could not be uploaded${failed}. Please remove or re-attach them and try again.`, {
                      toastId: "sendFailure",
                      position: "top-center",
                      transition: Bounce,
                    });
                    return;
                  }
                  emailObject.attachment = attachmentResult.returnValue;
                }
                defaultEncryptedMessage = await saveSenderEncryptedEmail(emailObject);
              }
              const result = await sendEmails(recipient.replace(/\s/g, '').split(","), cc.replace(/\s/g, '').split(","), bcc.replace(/\s/g, '').split(","), subject, localStorage.getItem("replyEmail"), isSavedOn, defaultEncryptedMessage, attachmentFiles, null, null, fromAddress);
              setLoader(false);
              if (result) {
                setonclear(true);
                // The reply body was read from localStorage on the line above; now that the send has
                // succeeded, drop it. Without this the cleartext reply lingered for the whole session
                // (the pagehide scrub skips bfcache-persisted pages, so it survived app-switches too).
                // Only on success — clearing on failure would destroy what the user typed.
                clearReplyBuffer();
                toast.success("Message Sent", {
                  position: "top-center",
                  transition: Bounce,
                });
                params.close(false);
              }
            }}> {senderLoader ? <div className="loader-ring-reply"></div> : `Send`} </div>
          </div>
        </div>
      </Modal>
      {loader &&
        <CompleteLoader isOpen={loader} text={"Please wait, your email is being sent, Going forward it will be fast."} />
      }
    </div>
  )
}


const CompleteLoader = (params) => {
  return (
    <div>
      <Modal
        open={params.isOpen}
        onOk={params.close}
        onCancel={params.close}
        closeIcon={null}
        footer={null}
        centered
        transitionName=""
        maskTransitionName=""
        className="modal-loader-common"
      >
        <Loader text={params.text} />
      </Modal>
    </div>
  );
};

const QuickSendModal = (params) => {
  const [user] = useState(cookies.get("userObject"));
  const [loader, setLoader] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState(null);
  const [quickSearch, setQuichSearch] = useState('');
  const [userList, setUserList] = useState([]);
  const [filterUserList, setFilterList] = useState([]);
  const [newUser, setNewUser] = useState('');
  const [alertInfo, setAlertInfo] = useState({});
  const placeHolder = userList.length ? "Search User" : "No User Found";

  async function quickAccessAction(userValue, index) {
    try {
      setLoader(true);
      if (!isQuickList(userValue)) {
        setLoadingIndex(index); // Set the index of the user being processed

        const host = getHostHelperContract();
        const functionParams = [user.name, userValue, userValue.split("@")[0]];
        const hash = await transactionAction(host, "saveQuickSend", functionParams, user.wallet, 'helper');
        setLoadingIndex(null);
        if (!hash) {
          // Don't close as if it worked — the contact was never added.
          notifyChainFailure('add that contact to Quick Send');
          return;
        }
        params.close();

      } else {
        setAlertInfo({ message: 'User already added in quick send', type: 'warning', autoClose: 5000, show: true });
      }
    }
    catch (err) {
      console.error(err)
    }
    finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    if (quickSearch.length) {
      const filter = userList.filter(value => value.includes(quickSearch));
      setFilterList(filter);
    } else {
      setFilterList(userList)
    }
  }, [quickSearch])

  useEffect(() => {
    setQuichSearch('');
    setNewUser('');
    setAlertInfo({ message: '', type: '', autoClose: null, show: false });
    setUserList(params.addressList);
    setFilterList(params.addressList);
  }, [params])

  const isQuickList = (userValue) => {
    return params.quickUserList.filter(list => list.userEmail == userValue).length == 0 ? false : true;
  }

  const validateUser = async (userValue) => {
    const emailRegex = /^[a-zA-Z0-9._@]+$/;
    if (!emailRegex.test(userValue)) {
      setAlertInfo({ message: 'Please enter a valid email address.', type: 'warning', autoClose: 5000, show: true });
      return false;
    }
    const atCount = (userValue.match(/@/g) || []).length;
    if (atCount !== 1) {
      setAlertInfo({ message: 'Please enter a valid email address.', type: 'warning', autoClose: 5000, show: true });
      return false;
    }
    return true;
  }

  const handleCloseAlert = () => {
    setAlertInfo({ ...alertInfo, show: false });
  };

  return (
    <div>
      <Modal className="modal-loader-quick-send" open={params.isOpen} onOk={params.close} onCancel={params.close} closeIcon={null} footer={null} style={{ width: '100%', maxWidth: '100%' }}>
        {alertInfo.show && <CustomAlert message={alertInfo.message} type={alertInfo.type} autoClose={alertInfo.autoClose} onClose={handleCloseAlert} />}
        <div className="quicksend-container">

          <div className="quick-send-header">
            <span>Add Contact for Quick Send</span>
            <div className="cursor-pointer" onClick={() => { params.close(); }}> {logo.close_svg} </div>
          </div>

          <div className="field-records-containers">
            <div className="input-field-search-user">
              <input className="input-search-user" type="text" onChange={(e) => { setAlertInfo({ show: false }); setQuichSearch(e.target.value) }} value={quickSearch} placeholder={placeHolder} />
              {logo.dmail_search_icon_header}
            </div>

            <div className="user-rendering-dynamic">
              <div className="user-section-add-user">
                <div className='user-details-section user-profile-render-data'>
                  <div className='user-profile-pic'>{newUser.charAt(0).toUpperCase() || '#'}</div>
                  <div className='user-profile-name inbox quick-send input-field-search-user'>
                    <input className="input-search-user" type="text" onChange={(e) => { setAlertInfo({ show: false }); setNewUser(e?.target?.value?.trim()?.toLowerCase()) }} value={newUser} placeholder="Add New User" maxLength="225" />
                  </div>
                  <div title="Add to Quick Access" className="add-user-icon-quick cursor-pointer" onClick={async () => {
                    const isValid = await validateUser(newUser);
                    if (isValid) { quickAccessAction(newUser, '#'); }
                  }}>
                    {loadingIndex === '#' ? <Loader normal={true} /> : logo.quick_add_svg}
                  </div>
                </div>
                <hr />
              </div>
              {filterUserList.map((userValue, index) => {

                const data = isQuickList(userValue);
                return (
                  <div className="user-section-add-user" key={index}>
                    <div className='user-details-section user-profile-render-data'>
                      <div className='user-profile-pic'>{getFirstLetterUppercase(userValue)}</div>
                      <div className='user-profile-name inbox quick-send'>
                        <div className='user-profile-username'>{userValue.split("@")[0]}</div>
                        <div className='user-profile-useremail'>{userValue}</div>
                      </div>
                      <div title={!data ? "Add to Quick Access" : "Already Added"} className={"add-user-icon-quick cursor-pointer" + (data ? " disable-value" : "")} onClick={async () => {
                        quickAccessAction(userValue, index);
                      }}>
                        {logo.quick_add_svg}
                      </div>
                    </div>
                    <hr />
                  </div>
                )
              })}
            </div>

          </div>
        </div>
      </Modal>

      <CompleteLoader isOpen={loader} text={'Please wait while we add the contact to your Quick Send list.'} />

    </div>
  );

}


const CreateFolderModal = (params) => {
  const [user] = useState(cookies.get("userObject"));
  const [folderColor, setFolderColor] = useState("green");
  const [folderName, setFolderName] = useState("");
  const [loader, setLoader] = useState(false);
  const [isToastShown, setIsToastShown] = useState(false);
  const availableColors = Object.keys(folders.folderIcon).map(folder => folder.replace('Folder', ''));
  const folderList = useSelector((state) => state.foldersList)
  const dispatch = useDispatch()
  async function addFolderOnChain() {
    if (folderName.trim() === "") {
      if (!toast.isActive("emptyError")) {
        toast.error("Please Enter Folder Name", {
          toastId: "emptyError",
          position: "top-center",
          transition: Bounce,
        });
      }
      return;
    }
    if (folderName.trim().length > 10) {
      if (!toast.isActive("lengthError")) {
        toast.error("Folder Name should not exceed 10 characters", {
          toastId: "lengthError",
          position: "top-center",
          transition: Bounce,
        });
      }
      return;
    }

    if (folderName.trim().length < 3) {
      if (!toast.isActive("minlengthError")) {
        toast.error("Folder Name should be at least 3 characters", {
          toastId: "minlengthError",
          position: "top-center",
          transition: Bounce,
        });
      }
      return;
    }

    const response = folderList.filter(data => {
      return data?.name === folderName.trim()
    })
    if (response.length > 0) {
      if (!toast.isActive("duplicateError")) {
        toast.error("Please Enter Unique Folder Name", {
          toastId: "duplicateError",
          position: "top-center",
          transition: Bounce,
        });
      }
      return;
    }


    setLoader(true);

    const folderJson = [{ key: "color", value: folderColor }];
    const functionParams = [user.name, folderName.trim(), getCurrentDate(), folderJson];
    const helperContract = getHostHelperContract();
    const hash = await transactionAction(helperContract, "createFolder", functionParams, user.wallet, 'helper');
    setLoader(false);
    if (!hash) {
      // Keep the dialog open with the typed name so the user can retry.
      notifyChainFailure('create that folder');
      return;
    }
    dispatch({ type: "FOLDER_UPDATE", payload: folderName.trim() })
    setDefault();
    params.close();

  }

  function setDefault() {
    setFolderColor("green");
    setFolderName("")
  }

  return (
    <div>
      <Modal
        className="modal-loader-quick-send"
        open={params.isOpen}
        onOk={params.close}
        onCancel={() => {
          params.close()
        }}
        closeIcon={null}
        footer={null}
        style={{ width: '100%', maxWidth: '100%' }}
      >
        <div className="quicksend-container">
          <div className="quick-send-header">
            <span>Add New Folder</span>
            <div className="cursor-pointer" onClick={params.close}>
              {logo.close_svg}
            </div>
          </div>

          <div className="folder-div-parent-container">
            <div className="folder-input-element">
              <div className="folder-name-update">Folder Name</div>
              <div className="folder-dynamic-content-type">
                {folders.folderIcon[`${folderColor}Folder`]}
                <div className="input-div-folder">
                  <input
                    className="input-folder"
                    type="text"
                    placeholder="Folder Name"
                    onChange={(e) => {
                      e.stopPropagation();
                      setFolderName(e.target.value)
                    }}
                    value={folderName}
                  />
                </div>
              </div>
            </div>

            <div className="folder-color-update-div">
              <div className="folder-name-update no-margin">Folder Color</div>
              <div className="folder-colder-update">
                {availableColors.map((color, index) => (
                  <div className="folder-type-color" key={index}>
                    {folders.folderIcon[`${color}Folder`]}

                    <div
                      className="radio-button-section"
                      onClick={() => setFolderColor(color)}
                    >
                      {folderColor === color ? (
                        getFolderIconsByColor(
                          folders.colorCombination[color].from,
                          folders.colorCombination[color].to
                        )
                      ) : (
                        folders['noChecked']
                      )}
                    </div>

                  </div>
                ))}
              </div>
            </div>

            <div className="add-folder-parent-div">
              <button className="add-folder-btn cursor-pointer" onClick={() => { addFolderOnChain() }}>
                Add Folder
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <CompleteLoader isOpen={loader} text={'Please wait, Creating the new folder in blockchain storage'} />
    </div>
  );
};

const FileReaderModel = (params) => {
  const [fileData, setFileData] = useState(null);
  const [fileType, setFileType] = useState('');
  const fileViewerRef = useRef(null);
  const getFileType = (fileName) => fileName.split(".").pop();
  useEffect(() => {
    if (params.file) {
      const fileURL = window.URL.createObjectURL(params.file);
      setFileData(fileURL);
      setFileType(getFileType(params.file.name));
      return () => window.URL.revokeObjectURL(fileURL);
    } else {
      setFileData(null);
      setFileType('');
    }
  }, [params.file]);

  useEffect(() => {
    if (!params.isOpen) {
      fileViewerRef.current = null;
    }
  }, [params.isOpen]);

  const renderFileViewer = (fileDatas, fileTypes) => {
    if (fileDatas && fileTypes) {

      try {
        return (
          <FileViewer
            ref={fileViewerRef}
            fileType={fileTypes}
            filePath={fileDatas}
            errorComponent={<p>Error loading file</p>}
            onError={(error) => console.error("FileViewer error: ", error)}
          />
        );
      } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
          endpoint: '',
          message: `FileName: common-alert.js, Fun: renderFileViewer, ${String(error.message || 'Unhandled error')}`,
          error: errorStr,
          userName: userName || '',
          level: 'warn',
        });
        console.error("Error rendering FileViewer: ", error);
        return <p>Error rendering file viewer</p>;
      }
    } else {
      return <p>No file selected</p>;
    }
  };

  return (
    <Modal
      className="parent-div-content-file-preview"
      open={params.isOpen}
      onOk={params.close}
      onCancel={params.close}
      closeIcon={null}
      footer={null}
    >
      <div className="quicksend-container">
        <div className="file-preview-header">
          <span>File Preview - {params.file.name}</span>
          <div onClick={params.close} className="header">
            {logo.compose_close}
          </div>
        </div>

        <div className="file-rendering-section">
          {renderFileViewer(fileData, fileType)}
        </div>
      </div>
    </Modal>
  );
};

const CustomAlert = ({ message, type, autoClose, onClose }) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(onClose, autoClose);
      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);
  return (
    <Alert message={message} type={type} showIcon onClose={onClose} closable />
  );
};

export {
  ReplyModal,
  CompleteLoader,
  QuickSendModal,
  CreateFolderModal,
  FileReaderModel,
  CustomAlert
}
